/**
 * ⭐⭐ PRD §71 (M5) e §77 (30/10/2026) · AS DUAS LEITURAS, NUMA FICHA SÓ.
 *
 * > *"O Hub lê e o GPT/CODEX lê, os dois retornam as informações para o Hub e o Hub preenche o 'formulário'."*
 * > — Richard, 30/10/2026 (plano da versão 2 aprovado)
 *
 * ⭐ §77: o Hub JUNTA as duas leituras CAMPO A CAMPO (`juntarCampos`), com a regra que a pesquisa sustenta (duas
 * leituras independentes que concordam quase nunca erram; a divergência é o sinal de conferir; marcar demais faz
 * confirmar sem olhar):
 * - leram o mesmo → preenchido, sem marca (a situação, o valor e a matrícula continuam com a página do papel);
 * - leram diferente → o valor que o PAPEL confirma (o fiscal achou no texto); sem confirmação de nenhum dos lados, o do
 *   GPT (mais preciso no ensaio M6); marcado "conferir" com as duas leituras, e a outra fica a um clique (`outra`).
 *   Nos campos de texto livre (endereço, tipo, cartório...) a outra fica a um clique, sem o amarelo;
 * - só um leu → preenchido, dizendo quem; nos campos que decidem, pede conferência;
 * - nenhum leu → em branco.
 * Sem o Codex (a cota, o login, o tempo, o caso que não vai a IA de fora), vale a leitura do Hub, com o motivo no topo e
 * os campos críticos pedindo conferência (a "leitura de reserva", parecer do Design de 28/10).
 *
 * ⛔ Nada de "carimbo de certo": a frase do topo diz quem leu e como juntou, e nada mais.
 */
import type { AtoDaMatricula, CampoLido, LeituraDeMatricula, LeituraPeloCodex, MatriculaProposta, OrigemDoCampo } from './api';

const MOTIVO: Record<string, string> = {
  cota: 'A cota do GPT acabou: a leitura foi só pelo Hub.',
  login: 'O login do GPT no servidor caiu: a leitura foi só pelo Hub (o login do redator precisa ser renovado).',
  tempo: 'O GPT demorou demais: a leitura foi só pelo Hub.',
  parado: 'O Hub reiniciou no meio da leitura: ela foi só pelo Hub. Leia de novo se quiser a do Codex.',
  outro: 'O GPT não respondeu de um jeito que o Hub lê: a leitura foi só pelo Hub.',
};

/** Os campos críticos: na leitura de reserva, todos pedem conferência (o Hub sozinho não teve segunda conferência). */
const CRITICOS = ['classificacao', 'valorDeclarado', 'numeroDaMatricula'] as const;

/** Os avisos do Hub que falam do PAPEL (e não da interpretação dele), e por isso valem também na leitura do Codex. */
const DO_PAPEL = /visualiza|n[aã]o vale como certid|PDF tem|leu s[oó]|cortad|marca d|parte do caso|outra pessoa|n[aã]o reconheci|n[aã]o achei o (documento|CPF|CNPJ)|resolutiva/i;

const comoNumero = (x: string | null) => {
  if (!x) return null;
  const n = Number(x.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};
const comoData = (x: string | null) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((x ?? '').trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
};

function deReserva(hub: LeituraDeMatricula, motivo: string): LeituraDeMatricula {
  const proposta = hub.proposta
    ? {
        ...hub.proposta,
        origens: {
          ...(hub.proposta.origens ?? {}),
          ...Object.fromEntries(
            CRITICOS.map((k) => {
              const o = hub.proposta?.origens?.[k];
              return [k, { pagina: o?.pagina ?? null, ato: o?.ato ?? null, trecho: o?.trecho ?? '', conferir: o?.conferir ?? 'Leitura de reserva (só no Hub): confira no papel.' }];
            }),
          ),
        },
      }
    : null;
  return { ...hub, proposta, pelo: 'hub', soNoHub: motivo };
}

/* ─── ⭐ §77: a junção campo a campo ──────────────────────────────────────────── */

type Tipo = 'texto' | 'numero' | 'data' | 'digitos' | 'escolha';
/** `decide`: a situação, o valor e a matrícula; `dado`: número, data e lugar; `texto`: escrita livre (sem amarelo). */
type Peso = 'decide' | 'dado' | 'texto';
type Campo = { k: CampoLido & keyof MatriculaProposta; tipo: Tipo; peso: Peso };

export const CAMPOS_DA_JUNCAO: Campo[] = [
  { k: 'classificacao', tipo: 'escolha', peso: 'decide' },
  { k: 'numeroDaMatricula', tipo: 'digitos', peso: 'decide' },
  { k: 'valorDeclarado', tipo: 'numero', peso: 'decide' },
  { k: 'ocorridoEm', tipo: 'data', peso: 'dado' },
  { k: 'avaliacao', tipo: 'numero', peso: 'dado' },
  { k: 'areaPrivativa', tipo: 'numero', peso: 'dado' },
  { k: 'areaConstruida', tipo: 'numero', peso: 'dado' },
  { k: 'areaDoTerreno', tipo: 'numero', peso: 'dado' },
  { k: 'cidade', tipo: 'texto', peso: 'dado' },
  { k: 'uf', tipo: 'texto', peso: 'dado' },
  { k: 'certidaoEm', tipo: 'data', peso: 'dado' },
  /* ⭐ §78: a Relação (a lista da ficha): o GPT pelo papel, o Hub pelo caso e pelo tipo do ato */
  { k: 'vinculoEntrePartes', tipo: 'escolha', peso: 'dado' },
  { k: 'matriculaSucessora', tipo: 'digitos', peso: 'dado' },
  { k: 'descricao', tipo: 'texto', peso: 'texto' },
  { k: 'tipoDoImovel', tipo: 'texto', peso: 'texto' },
  { k: 'modalidadeDeAquisicao', tipo: 'texto', peso: 'texto' },
  { k: 'cartorio', tipo: 'texto', peso: 'texto' },
  { k: 'qualidadeNoAto', tipo: 'texto', peso: 'texto' },
  { k: 'tabelionato', tipo: 'texto', peso: 'texto' },
  { k: 'proprietarioAtual', tipo: 'texto', peso: 'texto' },
  { k: 'motivoEncerramento', tipo: 'texto', peso: 'texto' },
  { k: 'efeitoPatrimonial', tipo: 'escolha', peso: 'texto' },
];

const ROTULO_DA_SITUACAO: Record<string, string> = {
  imovel_livre: 'livre',
  imovel_onerado: 'onerado',
  imovel_transmitido: 'transmitido',
  imovel_matricula_encerrada: 'matrícula encerrada',
  imovel_de_terceiro: 'em nome de terceiro',
  imovel_livre_empresa_do_grupo: 'livre, empresa do grupo',
};

function normal(x: unknown, tipo: Tipo): string | number | null {
  if (x === null || x === undefined || x === '') return null;
  if (tipo === 'numero') return typeof x === 'number' && Number.isFinite(x) ? Math.round(x * 100) / 100 : null;
  if (tipo === 'digitos') return String(x).replace(/\D/g, '').replace(/^0+/, '') || null;
  if (tipo === 'data' || tipo === 'escolha') return String(x).trim() || null;
  return (
    String(x)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, ' ')
      .trim() || null
  );
}

function iguais(a: string | number, b: string | number, tipo: Tipo): boolean {
  if (tipo === 'texto') return a === b || String(a).includes(String(b)) || String(b).includes(String(a));
  return a === b;
}

export function naTela(x: unknown, tipo: Tipo, k?: string): string {
  if (x === null || x === undefined || x === '') return '(nada)';
  if (k === 'classificacao') return `"${ROTULO_DA_SITUACAO[String(x)] ?? String(x)}"`;
  if (tipo === 'numero' && typeof x === 'number') {
    return k === 'valorDeclarado' || k === 'avaliacao'
      ? `R$ ${x.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : x.toLocaleString('pt-BR', { maximumFractionDigits: 4 });
  }
  if (tipo === 'data') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(x));
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }
  return `"${String(x)}"`;
}

/** O papel confirma este valor: o fiscal (ou o leitor) achou o trecho, e não deixou dúvida. */
const confirmado = (o: OrigemDoCampo | undefined) => Boolean(o && o.trecho && !o.conferir);
const vazia = (): OrigemDoCampo => ({ pagina: null, ato: null, trecho: '', conferir: null });

export type JuncaoDosCampos = { proposta: MatriculaProposta; divergentes: number; soUm: number };

/**
 * ⭐ §77: as duas propostas viram UMA, campo a campo. `codex` já vem conferida pelo fiscal (as origens dizem se o papel
 * mostra cada valor); `hub` traz as origens do próprio leitor.
 */
export function juntarCampos(hub: MatriculaProposta | null, codex: MatriculaProposta): JuncaoDosCampos {
  const proposta: MatriculaProposta = { ...codex, origens: { ...(codex.origens ?? {}) } };
  const origens = proposta.origens!;
  const escrever = proposta as unknown as Record<string, unknown>;
  let divergentes = 0;
  let soUm = 0;
  for (const { k, tipo, peso } of CAMPOS_DA_JUNCAO) {
    const hv = hub ? (hub[k] as unknown) : null;
    const cv = codex[k] as unknown;
    const nh = normal(hv, tipo);
    const nc = normal(cv, tipo);
    const oh = hub?.origens?.[k];
    const oc = codex.origens?.[k];
    if (nh === null && nc === null) continue;
    if (nh !== null && nc !== null && iguais(nh, nc, tipo)) {
      /* concordaram: sem marca; "não achado no documento" num campo que decide continua (o erro que os dois dividem) */
      const base = oc?.trecho ? oc : (oh ?? oc);
      const manter = peso === 'decide' && oc?.conferir && /N[aã]o achado no documento/.test(oc.conferir) ? oc.conferir : null;
      origens[k] = { ...(base ?? vazia()), conferir: manter, quem: 'os-dois' };
      continue;
    }
    if (nh === null || nc === null) {
      const de = nc !== null ? 'gpt' : 'hub';
      const base = de === 'gpt' ? oc : oh;
      escrever[k] = de === 'gpt' ? cv : hv;
      const conferir =
        base?.conferir ?? (peso === 'decide' ? `Só ${de === 'gpt' ? 'o GPT' : 'o Hub'} leu este campo: confira no papel.` : null);
      origens[k] = { ...(base ?? vazia()), conferir, quem: de };
      soUm += 1;
      continue;
    }
    /* divergiram: vale o que o papel confirma; sem confirmação dos dois lados (ou dos dois), o do GPT */
    const usaHub = confirmado(oh) && !confirmado(oc);
    const outra = usaHub ? cv : hv;
    escrever[k] = usaHub ? hv : cv;
    const porQue = usaHub ? 'Vale a do Hub (o papel confirma).' : confirmado(oc) ? 'Vale a do GPT (o papel confirma).' : 'Vale a do GPT (o papel não confirma nenhuma).';
    origens[k] = {
      ...((usaHub ? oh : oc) ?? vazia()),
      conferir: peso === 'texto' ? null : `Hub: ${naTela(hv, tipo, k)} · GPT: ${naTela(cv, tipo, k)}. ${porQue}`,
      quem: usaHub ? 'hub' : 'gpt',
      outra: { valor: outra as string | number, de: usaHub ? 'gpt' : 'hub', naTela: naTela(outra, tipo, k) },
    };
    if (peso !== 'texto') divergentes += 1;
  }
  /* o número da matrícula vai às duas colunas (§76), e a identificação acompanha o que ficou */
  if (proposta.numeroDaMatricula && !proposta.identificacao) proposta.identificacao = proposta.numeroDaMatricula;
  if (proposta.percentual === null && hub?.percentual !== null && hub?.percentual !== undefined) proposta.percentual = hub.percentual;
  /* as listas: as do GPT; sem nenhuma, as do Hub */
  if (proposta.onus.length === 0 && hub?.onus.length) proposta.onus = hub.onus;
  if (!proposta.avaliacoes?.length && hub?.avaliacoes?.length) proposta.avaliacoes = hub.avaliacoes;
  if (!proposta.partes?.length && hub?.partes?.length) proposta.partes = hub.partes;
  return { proposta, divergentes, soUm };
}

export function leituraCombinada(hub: LeituraDeMatricula, codex: LeituraPeloCodex | null): LeituraDeMatricula {
  if (!codex || codex.situacao !== 'pronto' || !codex.proposta) {
    const motivo = !codex || codex.situacao === 'na_fila' || codex.situacao === 'lendo'
      ? 'O GPT passou do tempo da tela: a leitura foi só pelo Hub.'
      : MOTIVO[codex.motivo ?? 'outro'] ?? MOTIVO['outro']!;
    return deReserva(hub, motivo);
  }
  const { proposta: p, divergentes } = juntarCampos(hub.proposta, codex.proposta);
  const atos: AtoDaMatricula[] = codex.atos.length
    ? codex.atos
        .filter((a) => a.mapa !== 'nao_existe')
        .map((a) => ({
          marca: a.marca,
          especie: a.marca.startsWith('AV') ? 'averbacao' : 'registro',
          data: comoData(a.data),
          natureza: a.tipo,
          valor: comoNumero(a.valor),
          trecho: '',
          papel: a.papel && !/n[aã]o informado/i.test(a.papel) ? a.papel : null,
          valorDeclarado: null,
          efeito: null,
        }))
    : hub.atos;
  /* a cláusula resolutiva dita uma vez só: a do Hub (com a marca do ato), quando ele a achou */
  const hubViuAResolutiva = hub.ressalvas.some((r) => /resolutiva/i.test(r));
  const ressalvas = [
    ...(codex.canario ? ['⚠️ O Codex achou no documento algo que parece uma ordem a quem lê: confira a leitura inteira com o PDF.'] : []),
    ...codex.avisos.filter((a) => !/parece uma ordem/.test(a) && !(hubViuAResolutiva && /^A compra traz cl[aá]usula resolutiva/.test(a))),
    ...p.ressalvas,
    ...hub.ressalvas.filter((r) => DO_PAPEL.test(r)),
  ];
  return {
    ...hub,
    matricula: (p.numeroDaMatricula ?? p.identificacao ?? hub.matricula) || hub.matricula,
    cartorio: p.cartorio ?? hub.cartorio,
    classificacaoSugerida: p.classificacao,
    porQue:
      divergentes > 0
        ? `Lido pelo Hub e pelo GPT; ${divergentes} ${divergentes === 1 ? 'campo divergiu e está marcado' : 'campos divergiram e estão marcados'}.`
        : 'Lido pelo Hub e pelo GPT; os dois concordaram nos campos que leram.',
    percentual: p.percentual,
    imovel: {
      ...hub.imovel,
      descricao: p.descricao ?? hub.imovel.descricao,
      tipo: p.tipoDoImovel ?? hub.imovel.tipo,
      cidade: p.cidade ?? hub.imovel.cidade,
      uf: p.uf ?? hub.imovel.uf,
      areaPrivativa: p.areaPrivativa,
      areaConstruida: p.areaConstruida,
      areaDoTerreno: p.areaDoTerreno,
    },
    atos,
    onus: p.onus.map((o, i) => ({
      natureza: o.natureza,
      credor: o.credor,
      valor: o.valor,
      constituidoEm: o.constituidoEm,
      cancelado: /cancel|quitad/i.test(o.situacao ?? ''),
      marca: `onus-${i}`,
    })),
    avaliacoes: (p.avaliacoes ?? []).flatMap((a) => (a.modalidade ? [{ modalidade: a.modalidade, valor: a.valor, referencia: a.referencia ?? 'valor declarado no documento' }] : [])),
    proposta: p,
    ressalvas: [...new Set(ressalvas)],
    pelo: 'codex',
    soNoHub: null,
    modelo: `${codex.modelo} · ${codex.nivel}`,
    canario: codex.canario,
  };
}
